import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronDown, X, Loader2 } from 'lucide-react';
import { useFieldSuggestions, highlightMatch } from '../../hooks/useFieldSuggestions';

/**
 * AutocompleteTextField - A combobox/autocomplete input component
 * 
 * Features:
 * - Shows dropdown on click with all available suggestions
 * - Filters suggestions as user types
 * - Allows free text entry (not restricted to suggestions)
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Highlights matching text in suggestions
 * 
 * @param {object} props
 * @param {string} props.entityName - Entity name for fetching suggestions (e.g., 'lead', 'customer')
 * @param {string} props.fieldName - Field name for fetching suggestions
 * @param {string} props.value - Current input value
 * @param {function} props.onChange - Callback when value changes
 * @param {string} props.placeholder - Input placeholder text
 * @param {string} props.className - Additional CSS classes for the input
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.id - Input element ID
 * @param {string} props.name - Input element name
 * @param {object} props.suggestConfig - Configuration for suggestions
 * @param {boolean} props.suggestConfig.enabled - Whether suggestions are enabled (default: true)
 * @param {number} props.suggestConfig.minChars - Min chars before searching (default: 0)
 * @param {number} props.suggestConfig.maxSuggestions - Max suggestions to show (default: 20)
 */
const AutocompleteTextField = ({
  entityName,
  fieldName,
  value = '',
  onChange,
  placeholder = '',
  className = '',
  disabled = false,
  required = false,
  id,
  name,
  suggestConfig = {},
  style,
  ...restProps
}) => {
  const {
    enabled = true,
    minChars = 0,
    maxSuggestions = 20
  } = suggestConfig;

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [inputValue, setInputValue] = useState(value || '');

  // Refs
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Fetch suggestions using the hook
  const {
    suggestions: allSuggestions,
    loading,
    error,
    search: searchSuggestions,
    loadAll
  } = useFieldSuggestions(entityName, fieldName, {
    enabled: enabled && !disabled,
    limit: maxSuggestions,
    minChars,
    debounceMs: 200
  });

  // Filter suggestions based on current input
  const filteredSuggestions = useMemo(() => {
    if (!inputValue || !inputValue.trim()) {
      return allSuggestions;
    }
    const term = inputValue.toLowerCase().trim();
    return allSuggestions.filter(s => 
      String(s).toLowerCase().includes(term)
    ).slice(0, maxSuggestions);
  }, [allSuggestions, inputValue, maxSuggestions]);

  // Sync input value with prop value
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const item = dropdownRef.current.children[highlightedIndex];
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Handle input change
  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setHighlightedIndex(-1);
    
    // Open dropdown when typing
    if (!isOpen) {
      setIsOpen(true);
    }

    // Search for suggestions
    searchSuggestions(newValue);

    // Call parent onChange
    if (onChange) {
      onChange(e);
    }
  }, [isOpen, onChange, searchSuggestions]);

  // Handle suggestion selection
  const handleSelectSuggestion = useCallback((suggestion) => {
    setInputValue(suggestion);
    setIsOpen(false);
    setHighlightedIndex(-1);

    // Call parent onChange with synthetic event
    if (onChange) {
      const syntheticEvent = {
        target: {
          name: name || fieldName,
          value: suggestion
        }
      };
      onChange(syntheticEvent);
    }
  }, [onChange, name, fieldName]);

  // Handle input focus
  const handleFocus = useCallback(() => {
    if (enabled && !disabled) {
      setIsOpen(true);
      // Load all suggestions on focus if we don't have any
      if (allSuggestions.length === 0) {
        loadAll();
      }
    }
  }, [enabled, disabled, allSuggestions.length, loadAll]);

  // Handle input click (open dropdown)
  const handleClick = useCallback(() => {
    if (enabled && !disabled && !isOpen) {
      setIsOpen(true);
      loadAll();
    }
  }, [enabled, disabled, isOpen, loadAll]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setIsOpen(true);
        loadAll();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      
      case 'Enter':
        if (highlightedIndex >= 0 && highlightedIndex < filteredSuggestions.length) {
          e.preventDefault();
          handleSelectSuggestion(filteredSuggestions[highlightedIndex]);
        }
        break;
      
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      
      case 'Tab':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      
      default:
        break;
    }
  }, [isOpen, highlightedIndex, filteredSuggestions, handleSelectSuggestion, loadAll]);

  // Handle clear button
  const handleClear = useCallback((e) => {
    e.stopPropagation();
    setInputValue('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    
    if (onChange) {
      const syntheticEvent = {
        target: {
          name: name || fieldName,
          value: ''
        }
      };
      onChange(syntheticEvent);
    }
    
    inputRef.current?.focus();
  }, [onChange, name, fieldName]);

  // Handle dropdown toggle button
  const handleToggleDropdown = useCallback((e) => {
    e.stopPropagation();
    if (disabled) return;
    
    if (isOpen) {
      setIsOpen(false);
      setHighlightedIndex(-1);
    } else {
      setIsOpen(true);
      loadAll();
      inputRef.current?.focus();
    }
  }, [disabled, isOpen, loadAll]);

  // Render highlighted text (matching search term)
  const renderHighlightedText = useCallback((text) => {
    const parts = highlightMatch(text, inputValue);
    return parts.map((part, index) => (
      part.isMatch ? (
        <span key={index} className="font-semibold bg-yellow-100 dark:bg-yellow-900/30 rounded px-0.5">{part.text}</span>
      ) : (
        <span key={index}>{part.text}</span>
      )
    ));
  }, [inputValue]);

  // Don't render suggestions UI if not enabled
  if (!enabled) {
    return (
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name || fieldName}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          if (onChange) onChange(e);
        }}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={className}
        style={style}
        {...restProps}
      />
    );
  }

  return (
    <div ref={containerRef} className="relative" style={{ display: 'inline-block', width: '100%', ...style }}>
      <div className="relative flex items-center">
        <input
          ref={inputRef}
          type="text"
          id={id}
          name={name || fieldName}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={`${className} pr-14`}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls={`${id || fieldName}-listbox`}
          {...restProps}
        />
        
        {/* Action buttons */}
        <div className="absolute right-1 flex items-center gap-0.5">
          {/* Loading indicator */}
          {loading && (
            <span className="p-1 text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
            </span>
          )}
          
          {/* Clear button */}
          {inputValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
              tabIndex={-1}
              aria-label="Clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          
          {/* Dropdown toggle */}
          <button
            type="button"
            onClick={handleToggleDropdown}
            disabled={disabled}
            className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none disabled:opacity-50"
            tabIndex={-1}
            aria-label="Toggle suggestions"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          id={`${id || fieldName}-listbox`}
          role="listbox"
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          style={{ minWidth: '100%' }}
        >
          {error && (
            <div className="px-3 py-2 text-sm text-red-500 dark:text-red-400">
              Error loading suggestions
            </div>
          )}
          
          {!error && filteredSuggestions.length === 0 && !loading && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 italic">
              {inputValue ? 'No matching suggestions' : 'No suggestions available'}
            </div>
          )}
          
          {!error && filteredSuggestions.map((suggestion, index) => (
            <div
              key={suggestion}
              role="option"
              aria-selected={highlightedIndex === index}
              className={`px-3 py-2 cursor-pointer text-sm transition-colors text-gray-700 dark:text-gray-200 ${
                highlightedIndex === index 
                  ? 'bg-blue-100 dark:bg-blue-900/40' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${
                inputValue && suggestion.toLowerCase() === inputValue.toLowerCase()
                  ? 'font-medium bg-blue-50 dark:bg-blue-900/20'
                  : ''
              }`}
              onClick={() => handleSelectSuggestion(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {renderHighlightedText(suggestion)}
            </div>
          ))}
          
          {loading && filteredSuggestions.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading suggestions...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteTextField;
